/**
 * Reminder Hooks - T128
 *
 * TanStack Query hooks for payment reminder operations
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type { CreateReminderInput, UpdateReminderInput, ReminderListQuery } from '@armard/shared';

// ─────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────

export interface PaymentReminder {
  id: string;
  invoiceId: string;
  reminderLevel: number;
  reminderDate: string;
  dueDate: string;
  reminderFee: string;
  interestRate: string | null;
  interestAmount: string;
  totalDue: string;
  notes: string | null;
  pdfGeneratedAt: string | null;
  pdfStoragePath: string | null;
  createdAt: string;
  updatedAt: string;
  invoice?: {
    id: string;
    invoiceNumber: string;
    customerId: string;
    grossTotal: string;
    dueDate: string;
    status: string;
    customer?: {
      id: string;
      companyName: string;
    };
  };
}

export interface ReminderListResult {
  reminders: PaymentReminder[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ─────────────────────────────────────────────────────────────
// QUERY KEYS
// ─────────────────────────────────────────────────────────────

export const reminderKeys = {
  all: ['reminders'] as const,
  lists: () => [...reminderKeys.all, 'list'] as const,
  list: (query: Partial<ReminderListQuery>) => [...reminderKeys.lists(), query] as const,
  forInvoice: (invoiceId: string) => [...reminderKeys.all, 'invoice', invoiceId] as const,
  details: () => [...reminderKeys.all, 'detail'] as const,
  detail: (id: string) => [...reminderKeys.details(), id] as const,
};

// ─────────────────────────────────────────────────────────────
// REMINDER HOOKS
// ─────────────────────────────────────────────────────────────

/**
 * Hook to list reminders with pagination
 */
export function useReminderList(query: Partial<ReminderListQuery> = {}) {
  const fullQuery = {
    page: query.page ?? 1,
    limit: query.limit ?? 40,
    ...query,
  };

  return useQuery({
    queryKey: reminderKeys.list(fullQuery),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(fullQuery.page));
      params.set('limit', String(fullQuery.limit));
      if (fullQuery.invoiceId) params.set('invoiceId', fullQuery.invoiceId);
      if (fullQuery.level) params.set('level', String(fullQuery.level));

      return api.get<ReminderListResult>(`/reminders?${params.toString()}`);
    },
  });
}

/**
 * Hook to get reminders for a specific invoice
 */
export function useRemindersForInvoice(invoiceId: string) {
  return useQuery({
    queryKey: reminderKeys.forInvoice(invoiceId),
    queryFn: () => api.get<PaymentReminder[]>(`/reminders/invoice/${invoiceId}`),
    enabled: !!invoiceId,
  });
}

/**
 * Hook to get a single reminder by ID
 */
export function useReminder(id: string) {
  return useQuery({
    queryKey: reminderKeys.detail(id),
    queryFn: () => api.get<PaymentReminder>(`/reminders/${id}`),
    enabled: !!id,
  });
}

/**
 * Hook to create a reminder
 */
export function useCreateReminder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateReminderInput) => api.post<PaymentReminder>('/reminders', input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: reminderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: reminderKeys.forInvoice(data.invoiceId) });
      // Also invalidate invoice list since status may have changed
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

/**
 * Hook to update a reminder
 */
export function useUpdateReminder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: UpdateReminderInput & { id: string }) =>
      api.patch<PaymentReminder>(`/reminders/${id}`, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: reminderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: reminderKeys.forInvoice(data.invoiceId) });
      queryClient.setQueryData(reminderKeys.detail(data.id), data);
    },
  });
}

/**
 * Hook to delete a reminder
 */
export function useDeleteReminder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/reminders/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reminderKeys.lists() });
      // Note: We don't know the invoiceId here, so invalidate all invoice reminders
      queryClient.invalidateQueries({ queryKey: ['reminders'] });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// PDF DOWNLOAD
// ─────────────────────────────────────────────────────────────

/**
 * Download reminder PDF
 */
export function downloadReminderPdf(reminderId: string): void {
  window.open(`/api/v1/reminders/${reminderId}/pdf`, '_blank');
}

/**
 * Preview reminder PDF
 */
export function previewReminderPdf(reminderId: string): void {
  window.open(`/api/v1/reminders/${reminderId}/pdf/preview`, '_blank');
}

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────

export const reminderLevelLabels: Record<number, string> = {
  1: '1. Mahnung',
  2: '2. Mahnung',
  3: 'Letzte Mahnung',
};

export const reminderLevelColors: Record<number, string> = {
  1: 'bg-yellow-500/20 text-yellow-400',
  2: 'bg-orange-500/20 text-orange-400',
  3: 'bg-red-500/20 text-red-400',
};
